import {type OfferAlternative} from '../types/OfferAlternative';

/** The seller's deal rights on the Business Case, resolved like the legacy deal screen does. */
export type OfferAlternativePermissions = {
  canAddVehicleToSell: boolean;
  canChangeSaleVehicle: boolean;
  canRemoveSaleVehicle: boolean;
  canOrderConfiguration: boolean;
  canEditDiscount: boolean;
  /** ADD_UPSELL (edit upsell prices), which the backend requires for a new typed upsell line. */
  canAddUpsell: boolean;
  /** ADD_UPSELL_ITEM_FROM_CATALOGUE (manage upsell); the backend prices the catalogue line. */
  canAddUpsellFromCatalogue: boolean;
  /** ADD_UPSELL or EDIT_UPSELL, the legacy upsell row-edit gate. */
  canEditUpsell: boolean;
  canRemoveUpsell: boolean;
  /** Applying then replaces the deal vehicle, which needs the change-vehicle right. */
  hasSaleVehicle: boolean;
};

export type AlternativeRights = {
  canApply: boolean;
  canWithdraw: boolean;
  canChangeSource: boolean;
  canEditDiscount: boolean;
  canAddUpsell: boolean;
  canAddUpsellFromCatalogue: boolean;
  canEditUpsell: boolean;
  canRemoveUpsell: boolean;
};

/** `canApply` and `canWithdraw` keep their names but now include the deal rights. */
export type OfferAlternativeWithRights = OfferAlternative & AlternativeRights;

/**
 * The backend flags (`canEdit`, `canApply`, `canWithdraw`) reflect only the Business Case state.
 * Each action also needs the deal right it maps to on the legacy deal screen, which the backend
 * enforces as well. Refreshing from the same source changes no deal term, so `canEdit` is enough.
 */
export const getAlternativeRights = (
  alternative: OfferAlternative,
  permissions: OfferAlternativePermissions
): OfferAlternativeWithRights => {
  // Every applied alternative becomes the deal vehicle; a configuration is also ordered.
  const canUseSource =
    permissions.canAddVehicleToSell &&
    (alternative.kind !== 'CONFIGURATION' || permissions.canOrderConfiguration);

  return {
    ...alternative,
    canApply:
      alternative.canApply &&
      canUseSource &&
      (!permissions.hasSaleVehicle || permissions.canChangeSaleVehicle),
    canWithdraw: alternative.canWithdraw && permissions.canRemoveSaleVehicle,
    canChangeSource: alternative.canEdit && permissions.canChangeSaleVehicle,
    canEditDiscount: alternative.canEdit && permissions.canEditDiscount,
    canAddUpsell: alternative.canEdit && permissions.canAddUpsell,
    canAddUpsellFromCatalogue: alternative.canEdit && permissions.canAddUpsellFromCatalogue,
    canEditUpsell: alternative.canEdit && permissions.canEditUpsell,
    canRemoveUpsell: alternative.canEdit && permissions.canRemoveUpsell,
  };
};
